import { useGetContext, useSplitTests } from '@api/lendingAPI/lapiHooks';
import { getCountry } from '@iwIntlProvider/CountryProvider';
import { FormattedName } from '@iwIntlProvider/FormattedName';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconDownload } from '@iwoca/icons';
import { Button } from '@iwoca/orion';
import { FormattedMessage, useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Dispatch } from 'redux';

import styles from './SignDocumentAction.module.css';
import { TSignDocument } from './SignDocumentsHelper';
import { FUNDING_STEP_ACTION } from '../../Funding.actions';
import StepItem from '../stepItem/StepItem';

const DocumentTypeLabel = ({ documentType }: { documentType: string }) => {
    switch (documentType) {
        case 'master_loan_agreement':
            return (
                <FormattedMessage
                    id="_.draw_down.step_buttons.loan_agreement.label"
                    defaultMessage="Credit Agreement"
                />
            );
        case 'secci':
            return (
                <FormattedMessage
                    id="_.draw_down.step_buttons.secci.label"
                    defaultMessage="SECCI"
                />
            );
        case 'reasonable_explanation':
            return (
                <FormattedMessage
                    id="_.draw_down.step_buttons.reasonable_explanation.label"
                    defaultMessage="Pre-contract Explanation"
                />
            );
        case 'repeat_loan_agreement':
            return (
                <FormattedMessage
                    id="_.draw_down.step_buttons.repeat_loan_agreement.label"
                    defaultMessage="Credit Agreement"
                />
            );
        case 'companies_house_charge':
            return (
                <FormattedMessage
                    id="_.draw_down.step_buttons.companies_house_charge.label"
                    defaultMessage="Debenture / Scottish Bond"
                />
            );
        case 'refinancing_side_letter':
            return (
                <FormattedMessage
                    id="_.draw_down.step_buttons.refinancing_side_letter.label"
                    defaultMessage="Refinancing Side Letter"
                />
            );
        default:
            return null;
    }
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
    handleFundingStepAction: (step: TSignDocument) => {
        let stepType = 'SIGN_DOCUMENT';
        if (step.signatureMethod === 'WET') {
            stepType = 'SIGN_DOCUMENT_WET';
        }
        dispatch({
            type: FUNDING_STEP_ACTION,
            step: stepType,
            signDocumentAction: step
        });
    },
    downloadSignDocument: (step: TSignDocument) => {
        dispatch({
            type: FUNDING_STEP_ACTION,
            step: 'VIEW_SIGN_DOCUMENT',
            signDocumentAction: step
        });
    }
});

const SignDocumentAction = ({
    step,
    handleFundingStepAction,
    downloadSignDocument
}: {
    step: TSignDocument;
} & ReturnType<typeof mapDispatchToProps>) => {
    const intl = useIntl();
    const { data } = useGetContext();

    const nonApplicantPgConsentSplitTest = useSplitTests(
        'non_applicant_pg_consent'
    );

    const isAccessUser = data?.logged_in_user_type === 'access_user';
    const documentType = step.type.toLowerCase();
    const formattedPersonToSign = (
        <FormattedName
            possessive={true}
            firstName={step.personToSign?.first_name}
            lastName={step.personToSign?.last_name}
        />
    );

    const formattedPersonForCaption = (
        <FormattedName
            firstName={step.personToSign?.first_name}
            lastName={step.personToSign?.last_name}
        />
    );

    const requiresNonApplicantSigning =
        !isAccessUser && step.requiresNonApplicantSigning;

    const title =
        step.type === 'guarantee' ? (
            <FormattedMessage
                id="_.draw_down.step_buttons.guarantor_agreement.label"
                defaultMessage="{requiresNonApplicantSigning, select, true {{formattedPersonToSign}} false {Your} other {}} Personal Guarantee"
                values={{
                    requiresNonApplicantSigning,
                    formattedPersonToSign
                }}
            />
        ) : (
            <DocumentTypeLabel documentType={documentType} />
        );

    const caption =
        step.status === 'REQUIRED' ? (
            step.signatureMethod === 'WET' ? (
                <>
                    <FormattedMessage
                        id="_.draw_down.sign_documents.wetSignDocumentsFooter"
                        defaultMessage="Once you sign these documents please send a scanned copy to {documentsLink}"
                        values={{
                            // This value is only used in the UK translation
                            // DE show the postal address below instead.
                            documentsLink: (
                                <a
                                    className={styles.documentTypeFooterLink}
                                    href={`mailto:${intl.formatMessage({ id: '_.draw_down.sign_documents.emailAddress', defaultMessage: 'documents@iwoca.co.uk' })}`}
                                >
                                    <FormattedMessage
                                        id="_.draw_down.sign_documents.emailAddress"
                                        defaultMessage="documents@iwoca.co.uk"
                                    />
                                </a>
                            )
                        }}
                    />
                    {/* DE requires post address to be shown */}
                    {getCountry() === 'DE' && (
                        <div className={styles.postalAddress}>
                            <FormattedMessage
                                id="_.draw_down.sign_documents.companyName"
                                defaultMessage="Company name"
                                tagName="div"
                            />
                            <FormattedMessage
                                id="_.draw_down.sign_documents.addressLineOne"
                                defaultMessage="Address line one"
                                tagName="div"
                            />
                            <FormattedMessage
                                id="_.draw_down.sign_documents.addressLineTwo"
                                defaultMessage="Address line two"
                                tagName="div"
                            />
                            <FormattedMessage
                                id="_.draw_down.sign_documents.addressLineThree"
                                defaultMessage="N/A"
                                tagName="div"
                            />
                        </div>
                    )}
                </>
            ) : (
                <FormattedMessage
                    id="_.funding_requirements.sign_document_caption"
                    defaultMessage={`This document requires {requiresNonApplicantSigning, select,
        true {{formattedPersonToSign}}
        false {a}
        other {}
        } digital signature.`}
                    values={{
                        requiresNonApplicantSigning,
                        formattedPersonToSign
                    }}
                />
            )
        ) : requiresNonApplicantSigning ? (
            <FormattedMessage
                id="_.funding_requirements.sign_document_access_user_caption_completed"
                defaultMessage="{formattedPersonForCaption} signed the personal guarantee."
                values={{
                    formattedPersonForCaption
                }}
            />
        ) : (
            <FormattedMessage
                id="_.funding_requirements.sign_document_caption_completed"
                defaultMessage="Your signed document is available for download."
            />
        );

    return (
        <StepItem
            title={title}
            caption={caption}
            action={
                step.status === 'COMPLETED' ? (
                    <Button
                        variant="tertiary"
                        onClick={() => downloadSignDocument(step)}
                        aria-label="download document"
                    >
                        <IconDownload
                            height="24"
                            width="24"
                            fill="rgba(16, 47, 86, 1)"
                            xlinkTitle="download"
                        />
                    </Button>
                ) : nonApplicantPgConsentSplitTest &&
                  requiresNonApplicantSigning ? null : (
                    <Button
                        variant="primary"
                        onClick={() => {
                            tracking.ampli.fundingStepCtaClicked({
                                funding_step: 'Sign document'
                            });
                            handleFundingStepAction(step);
                        }}
                    >
                        {step.signatureMethod === 'WET' ? (
                            <FormattedMessage
                                id="_.funding_requirements.sign_document_action_wet"
                                defaultMessage="Download & Sign"
                            />
                        ) : (
                            <FormattedMessage
                                id="_.funding_requirements.sign_document_action"
                                defaultMessage="View & Sign"
                            />
                        )}
                    </Button>
                )
            }
        />
    );
};

// eslint-disable-next-line import/no-default-export
export default connect(null, mapDispatchToProps)(SignDocumentAction);
